import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HistoricoAgendamento, HistoricoAtendimento, HistoricoBuscaPaciente, HistoricoCupom, HistoricoNotaFiscal, HistoricoPlano } from '../../../../interfaces/historico-paciente';
import { PatientAdvance } from '../../../../interfaces/payment';
import { ContaPacienteResumo } from '../../../../interfaces/dados-pagamento';
import { HistoricoPacienteService } from '../../../../services/historico-paciente/historico-paciente.service';
import { AtendimentoService } from '../../../../services/atendimento/atendimento.service';
import { PaymentService } from '../../../../services/payment/payment.service';
import { BrandService } from '../../../../services/brand/brand.service';
import { PatientService } from '../../../../services/patient/patient.service';
import { adiantamentoEstornado, DIAS_HISTORICO_ADIANTAMENTOS } from '../../../../utils/pagamento-avulso.util';
import { montarComprovanteEspecie } from '../../../../utils/comprovante-especie.util';
import { montarReimpressaoCartaoPix } from '../../../../utils/comprovante-reimpressao-cartao.util';
import { openPrintWindow } from '../../../../utils/print-receipt.util';
import { BrlPipe } from '../../../../utils/brl.pipe';
import { agruparOrcamentos, filtrarContasPorStatus, FiltroStatusConta, OrcamentoAgrupado, rotuloPlano } from '../../../../utils/historico-paciente.util';
import { HpOrcamentosComponent } from './secoes/hp-orcamentos.component';
import { HpContasComponent } from './secoes/hp-contas.component';
import { HpNotasComponent } from './secoes/hp-notas.component';

export type Aba = 'orcamentos' | 'contas' | 'atendimentos' | 'adiantamentos' | 'notas' | 'agendamentos' | 'cupons';

const ABAS: Aba[] = ['orcamentos', 'contas', 'atendimentos', 'adiantamentos', 'notas', 'agendamentos', 'cupons'];
const MAX_ATENDIMENTOS = 180;
const ADIANTAMENTOS_NA_TELA = 10;
const AGENDAMENTOS_NA_TELA = 3;

function porAba<T>(v: T): Record<Aba, T> {
  return Object.fromEntries(ABAS.map(a => [a, v])) as Record<Aba, T>;
}

export interface PacienteHistorico {
  cdPessoaFisica: number;
  nome: string;
  cpf: string;
}

export interface AcaoAtendimentoHistorico extends PacienteHistorico {
  nrAtendimento: number;
}

export interface ContaHistorico {
  paciente: PacienteHistorico;
  conta: ContaPacienteResumo;
}

/**
 * Histórico do Paciente no estilo do "Detalhes do Paciente" do PRE: cabeçalho fixo com o
 * cartão e as seções, corpo rolando só a seção ativa. Pagar, vincular e ações de supervisor
 * sobem para o dashboard, que abre a janela de pagamento por cima.
 */
@Component({
  selector: 'app-historico-paciente',
  standalone: true,
  imports: [CommonModule, FormsModule, BrlPipe, HpOrcamentosComponent, HpContasComponent, HpNotasComponent],
  templateUrl: './historico-paciente.component.html',
  styleUrl: './historico-paciente.component.scss',
})
export class HistoricoPacienteComponent implements OnChanges {
  @Input() aberto = false;
  /** Paciente já escolhido (senha chamada); sem ele o modal abre na busca. */
  @Input() pacienteInicial: PacienteHistorico | null = null;
  @Input() username = '';
  @Input() cdEmpresa = '';
  @Input() supervisorConta = false;
  /** Mudou: recarrega contas e adiantamentos (depois de pagar ou vincular). */
  @Input() contasVersao = 0;
  @Input() avisoContas = '';
  /** Janela de pagamento por cima: esconde sem desmontar, para voltar na mesma seção. */
  @Input() pausado = false;
  @Output() fechar = new EventEmitter<void>();
  @Output() imprimirFicha = new EventEmitter<AcaoAtendimentoHistorico>();
  @Output() pagarConta = new EventEmitter<ContaHistorico>();
  @Output() vincularConta = new EventEmitter<ContaHistorico>();
  @Output() menuConta = new EventEmitter<{ ev: MouseEvent; conta: ContaPacienteResumo; paciente: PacienteHistorico }>();

  readonly STATUS_FILTRO: [string, string][] = [
    ['N', 'Normal'], ['CN', 'Confirmada'], ['M', 'Marcada'], ['AD', 'Atendido'], ['E', 'Executada'],
    ['FI', 'Finalizado'], ['C', 'Cancelada'], ['L', 'Livre'], ['RE', 'Remarcada'],
    ['F', 'Falta justificada'], ['I', 'Falta não justificada'],
  ];

  readonly SECOES: { id: Aba; rotulo: string }[] = [
    { id: 'orcamentos', rotulo: 'Orçamentos' },
    { id: 'contas', rotulo: 'Contas' },
    { id: 'atendimentos', rotulo: 'Atendimentos' },
    { id: 'adiantamentos', rotulo: 'Adiantamentos' },
    { id: 'notas', rotulo: 'Notas Fiscais' },
    { id: 'agendamentos', rotulo: 'Agendamentos' },
    { id: 'cupons', rotulo: 'Cupons' },
  ];

  readonly FILTROS_CONTA: { id: FiltroStatusConta; rotulo: string }[] = [
    { id: 'todos', rotulo: 'Todas' },
    { id: 'provisorio', rotulo: 'Provisório' },
    { id: 'definitivo', rotulo: 'Definitivo' },
    { id: 'definitivo_saldo', rotulo: 'Definitivo c/ saldo' },
  ];

  paciente: PacienteHistorico | null = null;
  busca = '';
  resultados: HistoricoBuscaPaciente[] = [];
  buscando = false;
  private timerBusca?: ReturnType<typeof setTimeout>;

  aba: Aba = 'agendamentos';
  filtroTipo = '';
  filtroStatus = '';
  filtroContas: FiltroStatusConta = 'todos';
  mostrarTodosAgendamentos = false;
  mostrarTodosAdiantamentos = false;

  /** Dados do cartão (person-by-filter); null até carregar. */
  pessoa: any = null;
  /** undefined: ainda não carregou ou falhou (fica sem etiqueta). */
  plano: HistoricoPlano | null | undefined = undefined;

  orcamentos: OrcamentoAgrupado[] = [];
  contas: ContaPacienteResumo[] = [];
  agendamentos: HistoricoAgendamento[] = [];
  atendimentos: HistoricoAtendimento[] = [];
  adiantamentos: PatientAdvance[] = [];
  notasFiscais: HistoricoNotaFiscal[] = [];
  cupons: HistoricoCupom[] = [];
  carregada = porAba(false);
  carregando = porAba(false);
  erro = porAba('');
  // Troca de paciente no meio de uma carga: a resposta antiga nao pode cair no paciente novo.
  private geracao = 0;
  // Recarga forcada com outra em andamento: so a resposta mais nova vale.
  private pedido = porAba(0);

  /** nrAtendimento da conta a destacar na seção Contas. */
  destacarConta: number | null = null;
  /** Atendimento buscado pelo número: fica destacado na lista. */
  atendimentoBuscado: number | null = null;

  unidades = new Map<number, string>();

  notasDoAtendimento: number | null = null;
  notas: HistoricoNotaFiscal[] = [];
  carregandoNotas = false;
  erroNotas = '';

  reimprimindo: number | null = null;
  erroReimpressao = '';

  constructor(
    private historicoService: HistoricoPacienteService,
    private atendimentoService: AtendimentoService,
    private paymentService: PaymentService,
    private brandService: BrandService,
    private patientService: PatientService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['aberto']?.currentValue === true) {
      this.carregarUnidades();
      this.aba = 'agendamentos';
      // Sem senha chamada abre sempre na busca: o paciente consultado antes nao pode reaparecer.
      if (this.pacienteInicial) {
        this.selecionar(this.pacienteInicial);
      } else {
        this.limparPaciente();
        this.busca = '';
        this.resultados = [];
      }
      return;
    }
    const versao = changes['contasVersao'];
    if (versao && !versao.firstChange && this.paciente) {
      this.carregar('contas', true);
      this.carregar('adiantamentos', true);
      // cupom usado no pagamento sai da lista
      this.carregar('cupons', true);
    }
  }

  private async carregarUnidades(): Promise<void> {
    if (this.unidades.size) return;
    try {
      const lista = await this.paymentService.getEstablishments(this.username, this.cdEmpresa || undefined);
      lista.forEach(u => this.unidades.set(Number(u.id), u.name));
    } catch { /* so perde o nome da unidade nos adiantamentos */ }
  }

  // ── Busca ───────────────────────────────────────────────

  onBusca(): void {
    clearTimeout(this.timerBusca);
    const termo = this.busca.trim();
    if (termo.length < 3) {
      this.resultados = [];
      return;
    }
    this.timerBusca = setTimeout(async () => {
      this.buscando = true;
      try {
        this.resultados = await this.historicoService.buscar(termo);
      } catch {
        this.resultados = [];
      } finally {
        this.buscando = false;
      }
    }, 400);
  }

  escolher(p: HistoricoBuscaPaciente): void {
    this.resultados = [];
    this.busca = '';
    const paciente = { cdPessoaFisica: Number(p.cdPessoaFisica), nome: p.nmPessoaFisica, cpf: p.nrCpf ?? '' };
    this.aba = p.nrAtendimento ? 'atendimentos' : 'agendamentos';
    this.selecionar(paciente, p.nrAtendimento ?? undefined);
  }

  selecionar(p: PacienteHistorico, nrAtendimento?: number): void {
    this.paciente = p;
    this.geracao++;
    this.atendimentoBuscado = nrAtendimento ?? null;
    this.destacarConta = null;
    this.filtroTipo = '';
    this.filtroStatus = '';
    this.filtroContas = 'todos';
    this.mostrarTodosAgendamentos = false;
    this.mostrarTodosAdiantamentos = false;
    this.limparDados();
    this.carregarCabecalho();
    this.carregar(this.aba, true);
    // Todas em paralelo (como o Promise.all do PRE) para os contadores aparecerem ja; cada uma com seu erro.
    for (const a of ABAS) {
      if (a !== this.aba) this.carregar(a);
    }
  }

  limparPaciente(): void {
    this.paciente = null;
    this.geracao++;
    this.limparDados();
  }

  private limparDados(): void {
    this.pessoa = null;
    this.plano = undefined;
    this.orcamentos = [];
    this.contas = [];
    this.agendamentos = [];
    this.atendimentos = [];
    this.adiantamentos = [];
    this.notasFiscais = [];
    this.cupons = [];
    this.carregada = porAba(false);
    // A carga do paciente anterior e descartada pela geracao; nao pode travar a do novo.
    this.carregando = porAba(false);
    this.erro = porAba('');
  }

  private async carregarCabecalho(): Promise<void> {
    if (!this.paciente) return;
    const cd = this.paciente.cdPessoaFisica;
    const minha = this.geracao;
    const pessoa = this.patientService.getPersonByFilter({ type: 2, filter: String(cd) })
      .then(r => { if (minha === this.geracao) this.pessoa = r ?? null; })
      .catch(() => { /* cartao fica so com nome e codigo */ });
    const plano = this.historicoService.plano(cd)
      .then(r => { if (minha === this.geracao) this.plano = r; })
      .catch(() => { /* sem etiqueta: "Particular" afirmaria o que nao se sabe */ });
    await Promise.all([pessoa, plano]);
  }

  // ── Seções ──────────────────────────────────────────────

  trocarAba(aba: Aba): void {
    this.aba = aba;
    this.destacarConta = null;
    this.carregar(aba);
  }

  atualizar(): void {
    this.carregar(this.aba, true);
  }

  /** null enquanto não carregou ou quando vazia. */
  contador(aba: Aba): number | null {
    if (!this.carregada[aba]) return null;
    const n = this.listaDa(aba).length;
    return n > 0 ? n : null;
  }

  private listaDa(aba: Aba): unknown[] {
    switch (aba) {
      case 'orcamentos': return this.orcamentos;
      case 'contas': return this.contas;
      case 'atendimentos': return this.atendimentos;
      case 'adiantamentos': return this.adiantamentos;
      case 'notas': return this.notasFiscais;
      case 'agendamentos': return this.agendamentos;
      case 'cupons': return this.cupons;
    }
  }

  async carregar(aba: Aba, forcar = false): Promise<void> {
    if (!this.paciente) return;
    // Clique repetido nao empilha consulta no banco; so a recarga forcada passa.
    if (!forcar && (this.carregada[aba] || this.carregando[aba])) return;
    const cd = this.paciente.cdPessoaFisica;
    const minha = this.geracao;
    const meu = ++this.pedido[aba];
    const vale = () => minha === this.geracao && meu === this.pedido[aba];
    this.carregando[aba] = true;
    this.erro[aba] = '';
    try {
      switch (aba) {
        case 'orcamentos': {
          const r = agruparOrcamentos(await this.historicoService.orcamentos(cd));
          if (vale()) this.orcamentos = r;
          break;
        }
        case 'contas': {
          const r = await this.atendimentoService.getContasPaciente(cd);
          if (vale()) this.contas = r;
          break;
        }
        case 'agendamentos': {
          const r = await this.historicoService.agendamentos(cd);
          if (vale()) this.agendamentos = r;
          break;
        }
        case 'atendimentos': {
          const r = await this.historicoService.atendimentos(cd);
          if (vale()) {
            this.atendimentos = r;
            this.rolarParaAtendimentoBuscado();
          }
          break;
        }
        case 'adiantamentos': {
          // Sem adiantamento o back responde 404; nao e erro.
          const r = await this.paymentService.getPatientAdvances(String(cd), DIAS_HISTORICO_ADIANTAMENTOS, 20)
            .catch(e => { if (e?.status === 404) return []; throw e; });
          if (vale()) this.adiantamentos = r;
          break;
        }
        case 'notas': {
          const r = await this.historicoService.notasFiscaisPaciente(cd);
          if (vale()) this.notasFiscais = r;
          break;
        }
        case 'cupons': {
          const r = await this.historicoService.cupons(cd);
          if (vale()) this.cupons = r;
          break;
        }
      }
      if (vale()) this.carregada[aba] = true;
    } catch (e: any) {
      if (vale()) {
        // O motivo (tempo esgotado) vem em notifications; feedback e o texto generico.
        this.erro[aba] = e?.status === 400
          ? (e?.error?.notifications?.[0]?.message ?? e?.error?.feedback ?? 'Não foi possível carregar. Tente de novo.')
          : 'Não foi possível carregar. Tente de novo.';
      }
    } finally {
      if (vale()) this.carregando[aba] = false;
    }
  }

  private rolarParaAtendimentoBuscado(): void {
    const nr = this.atendimentoBuscado;
    if (!nr || !this.atendimentos.some(a => a.nrAtendimento === nr)) return;
    // a linha so existe no DOM depois da proxima deteccao de mudancas
    setTimeout(() => document.querySelector(`[data-atendimento="${nr}"]`)?.scrollIntoView?.({ block: 'center' }));
  }

  // ── Cartão do paciente ──────────────────────────────────

  get linkAdmin(): string {
    return `https://admin.meucartaosim.com.br/clientes/${this.paciente?.cdPessoaFisica ?? ''}`;
  }

  get etiquetaPlano(): { texto: string; particular: boolean } | null {
    return this.plano === undefined ? null : rotuloPlano(this.plano);
  }

  get dadosCartao(): { cpf: string; nascimento: string; sexo: string; telefone: string; cep: string; email: string; endereco: string } {
    const p = this.pessoa ?? {};
    const end = p.address ?? {};
    const nasc = this.data(p.birthDate);
    const dd = (n: number) => String(n).padStart(2, '0');
    const nascimento = nasc
      ? `${dd(nasc.getDate())}/${dd(nasc.getMonth() + 1)}/${nasc.getFullYear()}${p.age != null ? ` · ${p.age} anos` : ''}`
      : '';
    const rua = [end.street, end.number].filter(v => v != null && String(v).trim() !== '').join(', ');
    const cidade = [end.city, end.state].filter(Boolean).join('/');
    const local = [end.neighborhood, cidade].filter(Boolean).join(', ');
    return {
      cpf: this.formatarCpf(this.paciente?.cpf || p.document) || '—',
      nascimento: nascimento || '—',
      sexo: this.sexo(p.gender) || '—',
      telefone: this.formatarTelefone(p.phone) || '—',
      cep: this.formatarCep(end.zipcode) || '—',
      email: p.email || '—',
      endereco: [rua, local].filter(Boolean).join(' — ') || '—',
    };
  }

  private sexo(g: string | null | undefined): string {
    const v = (g ?? '').trim().toUpperCase();
    if (v === 'F' || v === 'FEMININO') return 'Feminino';
    if (v === 'M' || v === 'MASCULINO') return 'Masculino';
    return g ?? '';
  }

  private formatarTelefone(t: string | null | undefined): string {
    const d = (t ?? '').replace(/\D/g, '');
    if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return t ?? '';
  }

  private formatarCep(c: string | null | undefined): string {
    const d = (c ?? '').replace(/\D/g, '');
    return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : (c ?? '');
  }

  // ── Contas ──────────────────────────────────────────────

  verConta(nrAtendimento: number): void {
    this.aba = 'contas';
    this.filtroContas = 'todos';
    this.destacarConta = nrAtendimento;
    this.carregar('contas');
  }

  /** Chips contam todas as contas carregadas; a janela de 40 dias só vale em "Todas" dentro da seção. */
  contarContas(f: FiltroStatusConta): number {
    return filtrarContasPorStatus(this.contas, f).length;
  }

  onPagar(conta: ContaPacienteResumo): void {
    if (this.paciente) this.pagarConta.emit({ paciente: this.paciente, conta });
  }

  onVincular(conta: ContaPacienteResumo): void {
    if (this.paciente) this.vincularConta.emit({ paciente: this.paciente, conta });
  }

  onMenu(e: { ev: MouseEvent; conta: ContaPacienteResumo }): void {
    if (this.paciente) this.menuConta.emit({ ...e, paciente: this.paciente });
  }

  // ── Datas e agrupamento por mês ─────────────────────────

  private static readonly MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho',
    'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
  private static readonly SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

  // O back manda dd/MM/yyyy nas abas novas e ISO nos adiantamentos.
  private data(s: string | null | undefined): Date | null {
    if (!s) return null;
    const br = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(s);
    if (br) return new Date(+br[3], +br[2] - 1, +br[1]);
    // yyyy-MM-dd sem hora seria lido em UTC e voltaria um dia no Brasil
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (iso) return new Date(+iso[1], +iso[2] - 1, +iso[3]);
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }

  /** yyyy-MM-dd da busca para dd/MM/yyyy. */
  diaMesAno(iso: string | null): string {
    const [a, m, d] = (iso ?? '').split('-');
    return a && m && d ? `${d}/${m}/${a}` : '';
  }

  diaMes(s: string): string {
    const d = this.data(s);
    return d ? `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}` : '';
  }

  diaSemana(s: string): string {
    const d = this.data(s);
    return d ? HistoricoPacienteComponent.SEMANA[d.getDay()] : '';
  }

  private agrupar<T>(lista: T[], dataDe: (t: T) => string): { titulo: string; itens: T[] }[] {
    const grupos: { titulo: string; itens: T[] }[] = [];
    for (const item of lista) {
      const d = this.data(dataDe(item));
      const titulo = d ? `${HistoricoPacienteComponent.MESES[d.getMonth()]} de ${d.getFullYear()}` : 'sem data';
      const ultimo = grupos[grupos.length - 1];
      if (ultimo?.titulo === titulo) ultimo.itens.push(item);
      else grupos.push({ titulo, itens: [item] });
    }
    return grupos;
  }

  get gruposAgendamentos() {
    return this.agrupar(this.agendamentosExibidos, a => a.dtAgenda);
  }

  get gruposAtendimentos() {
    return this.agrupar(this.atendimentos, a => a.dtEntrada);
  }

  get gruposAdiantamentos() {
    return this.agrupar(this.adiantamentosExibidos, a => a.dtAdiantamento);
  }

  // ── Agendamentos ────────────────────────────────────────

  get agendamentosFiltrados(): HistoricoAgendamento[] {
    return this.agendamentos.filter(a =>
      (!this.filtroTipo || a.tipo === this.filtroTipo) &&
      (!this.filtroStatus || a.ieStatusAgenda === this.filtroStatus));
  }

  get agendamentosExibidos(): HistoricoAgendamento[] {
    const f = this.agendamentosFiltrados;
    return this.mostrarTodosAgendamentos ? f : f.slice(0, AGENDAMENTOS_NA_TELA);
  }

  get qtAgendamentosOcultos(): number {
    return this.agendamentosFiltrados.length - this.agendamentosExibidos.length;
  }

  corStatus(ie: string): string {
    if (['AD', 'E', 'FI'].includes(ie)) return 'ok';
    if (ie === 'C') return 'cancelado';
    if (['N', 'CN', 'M'].includes(ie)) return 'pendente';
    return 'neutro';
  }

  corTipo(a: HistoricoAgendamento): string {
    const classif = (a.dsClassif ?? '').toLowerCase();
    if (classif.includes('retorno')) return 'retorno';
    if (classif.includes('proc')) return 'proc';
    return a.tipo === 'EXAME' ? 'exame' : 'consulta';
  }

  // ── Atendimentos ────────────────────────────────────────

  get qtAvulsos(): number {
    return this.atendimentos.filter(a => a.avulso).length;
  }

  get avisoAtendimento(): string {
    const nr = this.atendimentoBuscado;
    if (!nr || !this.carregada.atendimentos || this.atendimentos.some(a => a.nrAtendimento === nr)) return '';
    return `O atendimento #${nr} não está entre os ${MAX_ATENDIMENTOS} mais recentes.`;
  }

  /** Mesmo campo do selo "Sem conta" da linha. */
  temConta(a: HistoricoAtendimento): boolean {
    return a.ieSituacaoConta !== 'SEM_CONTA';
  }

  vinculo(a: HistoricoAtendimento): string {
    const partes: string[] = [];
    if (a.nrSeqConsulta) partes.push(`Consulta #${a.nrSeqConsulta}`);
    if (a.nrSeqExame) partes.push(`Exame #${a.nrSeqExame}`);
    return partes.join(' + ');
  }

  ficha(nrAtendimento: number): void {
    if (!this.paciente) return;
    this.imprimirFicha.emit({ ...this.paciente, nrAtendimento });
  }

  async abrirNotas(nrAtendimento: number): Promise<void> {
    this.notasDoAtendimento = nrAtendimento;
    this.notas = [];
    this.erroNotas = '';
    this.carregandoNotas = true;
    // Clique em NF de outro atendimento antes da resposta: a antiga nao pode cair no modal novo.
    const doAtendimento = () => this.notasDoAtendimento === nrAtendimento;
    try {
      const notas = await this.historicoService.notasFiscais(nrAtendimento);
      if (doAtendimento()) this.notas = notas;
    } catch {
      if (doAtendimento()) this.erroNotas = 'Não foi possível carregar as notas fiscais.';
    } finally {
      if (doAtendimento()) this.carregandoNotas = false;
    }
  }

  // ── Cupons ──────────────────────────────────────────────

  diasParaVencer(c: HistoricoCupom): number | null {
    const d = this.data(c.dtExpiracao);
    if (!d) return null;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    return Math.round((d.getTime() - hoje.getTime()) / 86_400_000);
  }

  // Quem vence primeiro aparece primeiro; sem validade vai para o fim.
  get cuponsOrdenados(): HistoricoCupom[] {
    const chave = (c: HistoricoCupom) => this.diasParaVencer(c) ?? Number.MAX_SAFE_INTEGER;
    return [...this.cupons].sort((a, b) => chave(a) - chave(b));
  }

  get qtCupons(): number {
    return this.cupons.reduce((s, c) => s + (c.quantidade || 1), 0);
  }

  get qtCuponsVencendo(): number {
    return this.cupons
      .filter(c => { const d = this.diasParaVencer(c); return d !== null && d <= 7; })
      .reduce((s, c) => s + (c.quantidade || 1), 0);
  }

  // Quantos de cada tipo, na ordem em que o operador costuma oferecer.
  get resumoTipos(): { tipo: string; rotulo: string; qt: number }[] {
    return (['V', 'P', 'F', 'R', ''] as const)
      .map(t => ({ tipo: t, rotulo: this.rotuloTipo(t), qt: this.cupons.filter(c => (c.tipo || '') === t).reduce((s, c) => s + (c.quantidade || 1), 0) }))
      .filter(r => r.qt > 0);
  }

  rotuloTipo(tipo: string): string {
    switch (tipo) {
      case 'V': return 'Grátis';
      case 'P': return 'Percentual';
      case 'F': return 'Valor fixo';
      case 'R': return 'Desconto em R$';
      default: return 'Sem tipo';
    }
  }

  // O valor grande do ticket muda de sentido com o tipo: "15%", "sai por R$ 49", "- R$ 50".
  valorTicket(c: HistoricoCupom): { rotulo: string; valor: string } {
    const brl = (v: number | null | undefined) => (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    switch (c.tipo) {
      case 'V': return { rotulo: 'zerado', valor: 'Grátis' };
      case 'P': return { rotulo: 'de desconto', valor: `${c.prDesconto ?? 0}%` };
      case 'F': return { rotulo: 'sai por', valor: brl(c.vlDesconto) };
      case 'R': return { rotulo: 'de desconto', valor: `- ${brl(c.vlDesconto)}` };
      default: return { rotulo: 'valor', valor: brl(c.valorCupom) };
    }
  }

  textoValidade(c: HistoricoCupom): string {
    const d = this.diasParaVencer(c);
    if (d === null) return 'Sem data de validade';
    if (d === 0) return 'Vence hoje';
    if (d === 1) return 'Vence amanhã';
    return `Vence em ${d} dias`;
  }

  // ── Adiantamentos (mesmas regras do pagamento avulso) ─────

  estornado(a: PatientAdvance): boolean {
    return adiantamentoEstornado(a);
  }

  transferido(a: PatientAdvance): boolean {
    const destino = (a.cdPessoaAdiant ?? '').trim();
    return destino !== '' && destino !== String(this.paciente?.cdPessoaFisica ?? '');
  }

  // Transferido tambem zera: o saldo passou a ser de outra pessoa.
  saldo(a: PatientAdvance): number {
    return this.estornado(a) || this.transferido(a) ? 0 : Math.max(a.vlSaldo ?? 0, 0);
  }

  situacao(a: PatientAdvance): { texto: string; classe: string } {
    if (this.estornado(a)) return { texto: 'Estornado', classe: 'cancelado' };
    if (this.transferido(a)) return { texto: 'Transferido', classe: 'transferido' };
    if (this.saldo(a) > 0) return { texto: 'Disponível', classe: 'ok' };
    return { texto: 'Utilizado', classe: 'neutro' };
  }

  get saldoDisponivel(): number {
    return this.adiantamentos
      .filter(a => !this.estornado(a) && !this.transferido(a))
      .reduce((s, a) => s + this.saldo(a), 0);
  }

  get qtComSaldo(): number {
    return this.adiantamentos.filter(a => this.saldo(a) > 0).length;
  }

  get adiantamentosExibidos(): PatientAdvance[] {
    return this.mostrarTodosAdiantamentos ? this.adiantamentos : this.adiantamentos.slice(0, ADIANTAMENTOS_NA_TELA);
  }

  get qtAdiantamentosAnteriores(): number {
    return this.adiantamentos.length - this.adiantamentosExibidos.length;
  }

  get totalAdiantado(): number {
    return this.adiantamentos.reduce((s, a) => s + (a.vlAdiantamento ?? 0), 0);
  }

  nomeUnidade(cd: number): string {
    return this.unidades.get(Number(cd)) ?? String(cd ?? '');
  }

  // Especie tem recibo e reimprime pelo recibo; cartao/Pix remonta com o que o adiantamento guarda.
  async reimprimir(a: PatientAdvance): Promise<void> {
    if (!this.paciente || this.reimprimindo) return;
    this.reimprimindo = a.nrAdiantamento;
    this.erroReimpressao = '';
    const marca = { logo: this.brandService.logo, cdEmpresa: this.brandService.cdEmpresa, marca: this.brandService.brandName };
    try {
      if (a.nrRecibo) {
        const recibo = await this.paymentService.getCashReceipt(a.nrRecibo);
        if (!recibo) {
          this.erroReimpressao = 'Não foi possível carregar os dados deste recibo.';
          return;
        }
        openPrintWindow(`Recibo ${recibo.nrRecibo} (2ª via)`, montarComprovanteEspecie({
          recibo, usuario: this.username, segundaVia: true, estornado: this.estornado(a), ...marca,
        }));
      } else {
        openPrintWindow(`Comprovante ${a.nrAdiantamento} (2ª via)`, montarReimpressaoCartaoPix({
          nmPaciente: this.paciente.nome,
          cpf: this.formatarCpf(this.paciente.cpf),
          valor: a.vlAdiantamento,
          dataHora: a.dtAdiantamento,
          nrAdiantamento: a.nrAdiantamento,
          observacao: a.dsObservacao,
          usuario: this.username,
          estornado: this.estornado(a),
          ...marca,
        }));
      }
    } catch {
      this.erroReimpressao = 'Erro ao gerar a 2ª via do comprovante.';
    } finally {
      this.reimprimindo = null;
    }
  }

  formatarCpf(cpf: string | null | undefined): string {
    const d = (cpf ?? '').replace(/\D/g, '');
    if (d.length !== 11) return cpf || '';
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  }
}
